import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

const PlayIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="48"
    height="48"
  >
    <path
      fillRule="evenodd"
      d={
        'M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 ' +
        '2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z'
      }
      clipRule="evenodd"
    />
  </svg>
);

const TestimonialCard = ({
  thumbnailUrl,
  videoUrl,
  avatarUrl,
  username,
  caption,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlayClick = () => {
    if (videoUrl) {
      setIsPlaying(true);
      // Auto-play after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      }, 0);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden aspect-[9/16] bg-gray-200">
      {/* Video Element - shown as preview when no thumbnail, or when playing */}
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className={`absolute inset-0 w-full h-full object-cover ${
            isPlaying || !thumbnailUrl ? 'block' : 'hidden'
          }`}
          playsInline
          onClick={handleVideoClick}
          onEnded={handleVideoEnd}
        />
      )}

      {/* Thumbnail Image (shown when not playing and thumbnail exists) */}
      {!isPlaying && thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt={`Testimonial from ${username}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Play Button Overlay (shown when not playing and has video) */}
      {!isPlaying && videoUrl && (
        <button
          type="button"
          onClick={handlePlayClick}
          className={
            'absolute inset-0 flex items-center justify-center cursor-pointer ' +
            'bg-black/20 hover:bg-black/30 transition-colors'
          }
          aria-label="Play video"
        >
          <div className="text-white opacity-90 transform hover:scale-110 transition-transform">
            <PlayIcon />
          </div>
        </button>
      )}

      {/* Caption Overlay (only when not playing) */}
      {!isPlaying && caption && (
        <div className="absolute bottom-16 left-0 right-0 px-4 text-center pointer-events-none">
          <p className="text-white text-sm font-medium drop-shadow-lg tracking-wide">
            {caption}
          </p>
        </div>
      )}

      {/* User Info (only when not playing) */}
      {!isPlaying && (
        <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 pointer-events-none">
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt={username}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <span className="text-white text-sm font-medium drop-shadow-lg">
            {username}
          </span>
        </div>
      )}
    </div>
  );
};

TestimonialCard.propTypes = {
  thumbnailUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  avatarUrl: PropTypes.string,
  username: PropTypes.string.isRequired,
  caption: PropTypes.string,
};

const SectionTestimonials = ({
  header = 'What Shippers Have to Say',
  items = [],
  containerClasses = '',
  backgroundColor = '#F3F5F6',
}) => {
  const scrollRef = useRef(null);

  return (
    <section className={`w-full py-2 px-6 lg:px-24 xl:px-44 2xl:px-60 ${containerClasses}`} style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-[#1C2938] text-base lg:text-[40px] font-bold text-center py-5 lg:py-10">
          {parse(header)}
        </h2>

        {/* Mobile Carousel */}
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto snap-x snap-mandatory lg:hidden pb-2 -mx-6"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {items.map((item, index) => (
            <div key={index} className="px-2 snap-start shrink-0 w-[80%]">
              <TestimonialCard
                thumbnailUrl={item.thumbnailImage?.url || item.image?.url}
                videoUrl={item.video?.url}
                avatarUrl={item.avatarImage?.url}
                username={item.username}
                caption={item.caption}
              />
            </div>
          ))}
          <div className="shrink-0 w-2" aria-hidden="true" />
        </div>


        {/* Desktop Grid */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-4">
          {items.map((item, index) => (
            <TestimonialCard
              key={index}
              thumbnailUrl={item.thumbnailImage?.url || item.image?.url}
              videoUrl={item.video?.url}
              avatarUrl={item.avatarImage?.url}
              username={item.username}
              caption={item.caption}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

SectionTestimonials.propTypes = {
  header: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string,
    }),
    thumbnailImage: PropTypes.shape({
      url: PropTypes.string,
    }),
    video: PropTypes.shape({
      url: PropTypes.string,
    }),
    avatarImage: PropTypes.shape({
      url: PropTypes.string,
    }),
    username: PropTypes.string.isRequired,
    caption: PropTypes.string,
  })),
  containerClasses: PropTypes.string,
  backgroundColor: PropTypes.string,
};

export default SectionTestimonials;
